import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { GET_WORK_ORDER_EXPENSES } from '../../graphql/work-order-expenses-queries';
import { 
  CREATE_WORK_ORDER_EXPENSE, 
  UPDATE_WORK_ORDER_EXPENSE, 
  DELETE_WORK_ORDER_EXPENSE 
} from '../../graphql/work-order-expenses-mutations';
import { GET_PRESET_EXPENSES, GET_WORK_ORDER_SERVICE_CHARGE } from '../../graphql/presetExpensesAndHourlyEmployees';

const WorkOrderExpensesSection = ({ 
  workOrderId, 
  currentUser,
  canEdit = true,
  workOrderStatus = 'OPEN'
}) => {
  // Check if user is admin
  const isAdmin = currentUser?.role === 'ADMIN';
  // Admins and Property Managers can see/edit billed amounts
  const canManageBilledAmount = currentUser?.role === 'ADMIN' || currentUser?.role === 'PROPERTY_MANAGER';
  
  const [newExpense, setNewExpense] = useState({
    title: '',
    baseCost: '',
    billedAmount: ''
  });
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [isSectionOpen, setIsSectionOpen] = useState(true);
  const [selectedPreset, setSelectedPreset] = useState('');

  // Query expenses
  const { data, loading, error, refetch } = useQuery(GET_WORK_ORDER_EXPENSES, {
    variables: { workOrderId },
    fetchPolicy: 'network-only',
    skip: !workOrderId
  });

  // Query preset expenses - only for admins (requires ADMIN role)
  const { data: presetData } = useQuery(GET_PRESET_EXPENSES, {
    skip: !isAdmin
  });
  const presetExpenses = presetData?.presetExpenses || [];

  // Query service charge setting - only for admins (requires ADMIN role)
  const { data: serviceChargeData } = useQuery(GET_WORK_ORDER_SERVICE_CHARGE, {
    skip: !isAdmin
  });
  const serviceCharge = serviceChargeData?.workOrderServiceCharge || 0;

  // Mutations
  const [createExpense, { loading: creating }] = useMutation(CREATE_WORK_ORDER_EXPENSE, {
    onCompleted: () => {
      toast.success('Expense added successfully');
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to add expense');
    }
  });

  const [updateExpense, { loading: updating }] = useMutation(UPDATE_WORK_ORDER_EXPENSE, {
    onCompleted: () => {
      toast.success('Expense updated successfully');
      setEditingExpenseId(null);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update expense');
    }
  });

  const [deleteExpense, { loading: deleting }] = useMutation(DELETE_WORK_ORDER_EXPENSE, {
    onCompleted: () => {
      toast.success('Expense deleted successfully');
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete expense');
    }
  });

  const expenses = data?.workOrderExpenses || [];

  // Calculate totals
  const totals = expenses.reduce(
    (acc, expense) => {
      acc.totalBaseCost += Number(expense.baseCost) || 0;
      acc.totalBilledAmount += Number(expense.billedAmount) || 0;
      return acc;
    },
    { totalBaseCost: 0, totalBilledAmount: 0 }
  );

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number(amount));
  };

  const resetForm = () => {
    setNewExpense({
      title: '',
      baseCost: '',
      billedAmount: ''
    });
    setSelectedPreset('');
  };

  // Handle preset expense selection
  const handlePresetSelect = (presetId) => {
    if (!presetId) {
      setSelectedPreset('');
      return;
    }
    const preset = presetExpenses.find(p => p.id === presetId);
    if (preset) {
      setNewExpense({
        title: preset.name,
        baseCost: preset.amount?.toString() || '',
        billedAmount: ''
      });
      setSelectedPreset(presetId);
    }
  };

  // Handle adding service charge
  const handleAddServiceCharge = async () => {
    if (serviceCharge <= 0) {
      toast.error('Service charge is not configured. Please set it in Preset Expenses settings.');
      return;
    }
    try {
      await createExpense({
        variables: {
          input: {
            workOrderId,
            title: 'Service Charge',
            baseCost: serviceCharge,
            billedAmount: serviceCharge
          }
        }
      });
    } catch (err) {
      // Error handled in mutation onError
    }
  };

  const handleAddExpense = async () => {
    // Validation - only title is required
    if (!newExpense.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    // Validate amounts if provided
    if (newExpense.baseCost && newExpense.baseCost < 0) {
      toast.error('Base cost cannot be negative');
      return;
    }
    if (newExpense.billedAmount && newExpense.billedAmount < 0) {
      toast.error('Billed amount cannot be negative');
      return;
    }

    try {
      const input = {
        workOrderId,
        title: newExpense.title.trim(),
      };

      // Only include amounts if they have values
      if (newExpense.baseCost) {
        input.baseCost = parseFloat(newExpense.baseCost);
      }
      if (newExpense.billedAmount) {
        input.billedAmount = parseFloat(newExpense.billedAmount);
      }

      await createExpense({
        variables: { input }
      });
    } catch (err) {
      // Error handled in mutation onError
    }
  };

  const handleUpdateExpense = async (expenseId, field, value) => {
    try {
      const updateInput = {};
      
      if (field === 'title') {
        if (!value.trim()) {
          toast.error('Title cannot be empty');
          return;
        }
        updateInput.title = value.trim();
      } else if (field === 'baseCost' || field === 'billedAmount') {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0) {
          toast.error('Please enter a valid amount');
          return;
        }
        updateInput[field] = numValue;
      }

      await updateExpense({
        variables: {
          id: expenseId,
          input: updateInput
        }
      });
    } catch (err) {
      // Error handled in mutation onError
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      await deleteExpense({
        variables: { id: expenseId }
      });
    } catch (err) {
      // Error handled in mutation onError
    }
  };

  const isReadOnly = !canEdit || workOrderStatus === 'CANCELED';
  const isProcessing = creating || updating || deleting;

  if (error) {
    return (
      <div className="bg-white shadow overflow-hidden rounded-lg mt-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Expenses</h3>
          <p className="mt-2 text-sm text-red-600">
            Error loading expenses. Please try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden rounded-lg mt-6">
      {/* Header */}
      <div 
        className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setIsSectionOpen(!isSectionOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Expenses</h3>
            {expenses.length > 0 && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {expenses.length} {expenses.length === 1 ? 'expense' : 'expenses'}
              </span>
            )}
          </div>
          <svg
            className={`h-5 w-5 text-gray-400 transform transition-transform duration-150 ${
              isSectionOpen ? 'rotate-180' : ''
            }`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>

      {/* Content */}
      {isSectionOpen && (
        <div>
          {/* Add Expense Form - Only show if can edit */}
          {!isReadOnly && (
            <div className="px-4 py-4 bg-gray-50 border-b border-gray-200">
              {/* Quick Add Section - Presets and Service Charge (Admin only) */}
              {isAdmin && (
                <div className="flex flex-col sm:flex-row sm:items-end space-y-2 sm:space-y-0 sm:space-x-3 mb-4 pb-4 border-b border-gray-200">
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Quick Add from Presets
                    </label>
                    <select
                      value={selectedPreset}
                      onChange={(e) => handlePresetSelect(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      disabled={isProcessing}
                    >
                      <option value="">-- Select a preset expense --</option>
                      {presetExpenses.map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.name} {preset.amount > 0 ? `(${formatCurrency(preset.amount)})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={handleAddServiceCharge}
                    disabled={isProcessing || serviceCharge <= 0}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center whitespace-nowrap"
                    title={serviceCharge > 0 ? `Add Service Charge (${formatCurrency(serviceCharge)})` : 'Service charge not configured'}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Service Charge {serviceCharge > 0 ? `(${formatCurrency(serviceCharge)})` : ''}
                  </button>
                </div>
              )}

              {/* Manual Entry Section */}
              <div className="flex flex-col sm:flex-row sm:items-end space-y-2 sm:space-y-0 sm:space-x-3">
                <div className="flex-1 min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newExpense.title}
                    onChange={(e) => {
                      setNewExpense({ ...newExpense, title: e.target.value });
                      setSelectedPreset(''); // Clear preset selection when manually typing
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter expense title"
                    disabled={isProcessing}
                  />
                </div>
                <div className="w-full sm:w-32">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Base Cost
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500 text-sm">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newExpense.baseCost}
                      onChange={(e) => setNewExpense({ ...newExpense, baseCost: e.target.value })}
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                      disabled={isProcessing}
                    />
                  </div>
                </div>
                {/* Billed Amount - Admin and Property Manager */}
                {canManageBilledAmount && (
                  <div className="w-full sm:w-32">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Billed Amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500 text-sm">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newExpense.billedAmount}
                        onChange={(e) => setNewExpense({ ...newExpense, billedAmount: e.target.value })}
                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                        disabled={isProcessing}
                      />
                    </div>
                  </div>
                )}
                <button
                  onClick={handleAddExpense}
                  disabled={!newExpense.title.trim() || isProcessing}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  title="Add Expense"
                >
                  {creating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Adding...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Expenses List */}
          {loading ? (
            <div className="px-4 py-8 text-center">
              <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-2 text-sm text-gray-500">Loading expenses...</p>
            </div>
          ) : expenses.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Base Cost
                      </th>
                      {/* Billed Amount column - Admin and Property Manager */}
                      {canManageBilledAmount && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Billed Amount
                        </th>
                      )}
                      {!isReadOnly && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {expenses.map((expense, index) => (
                      <tr key={expense.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {!isReadOnly && editingExpenseId === expense.id ? (
                            <input
                              type="text"
                              defaultValue={expense.title}
                              onBlur={(e) => {
                                if (e.target.value !== expense.title) {
                                  handleUpdateExpense(expense.id, 'title', e.target.value);
                                }
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={isProcessing}
                            />
                          ) : (
                            <div 
                              className={!isReadOnly ? 'cursor-pointer hover:text-blue-600' : ''}
                              onClick={() => !isReadOnly && setEditingExpenseId(expense.id)}
                            >
                              {expense.title}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {!isReadOnly && editingExpenseId === expense.id ? (
                            <div className="relative w-24">
                              <span className="absolute left-2 top-1 text-gray-500 text-sm">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                defaultValue={expense.baseCost}
                                onBlur={(e) => {
                                  if (parseFloat(e.target.value) !== expense.baseCost) {
                                    handleUpdateExpense(expense.id, 'baseCost', e.target.value);
                                  }
                                }}
                                className="w-full pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={isProcessing}
                              />
                            </div>
                          ) : (
                            <span className="font-medium">{formatCurrency(expense.baseCost)}</span>
                          )}
                        </td>
                        {/* Billed Amount cell - Admin and Property Manager */}
                        {canManageBilledAmount && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {!isReadOnly && editingExpenseId === expense.id ? (
                              <div className="relative w-24">
                                <span className="absolute left-2 top-1 text-gray-500 text-sm">$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  defaultValue={expense.billedAmount}
                                  onBlur={(e) => {
                                    if (parseFloat(e.target.value) !== expense.billedAmount) {
                                      handleUpdateExpense(expense.id, 'billedAmount', e.target.value);
                                    }
                                  }}
                                  className="w-full pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={isProcessing}
                                />
                              </div>
                            ) : (
                              <span className="font-medium">{formatCurrency(expense.billedAmount)}</span>
                            )}
                          </td>
                        )}
                        {!isReadOnly && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <button
                              onClick={() => handleDeleteExpense(expense.id)}
                              disabled={isProcessing}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                            >
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  {/* Totals Footer */}
                  <tfoot className="bg-blue-50 border-t-2 border-gray-300">
                    <tr>
                      <td className="px-6 py-4 text-sm font-bold text-gray-900">
                        Total
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(totals.totalBaseCost)}
                      </td>
                      {/* Total Billed Amount - Admin and Property Manager */}
                      {canManageBilledAmount && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                          {formatCurrency(totals.totalBilledAmount)}
                        </td>
                      )}
                      {!isReadOnly && <td className="px-6 py-4"></td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          ) : (
            /* Empty State */
            <div className="px-6 py-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No expenses</h3>
              <p className="mt-1 text-sm text-gray-500">
                {isReadOnly 
                  ? 'No expenses have been added to this work order yet.' 
                  : 'Use the form above to add your first expense.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkOrderExpensesSection;

